/*
 * Newsletter signup wrapper around a Shopify `{% form 'customer' %}`.
 *
 * The form is submitted NATIVELY. Shopify's storefront spam protection
 * (hCaptcha) intercepts customer forms, solves the challenge, then calls
 * form.submit() itself and Shopify redirects back to the same page with
 * `?customer_posted=true`. A fetch()-based submit can't ride along with
 * that flow (it is rejected with 400 without the captcha token, and the
 * page navigates away anyway), so instead we remember which form was
 * submitted and restore its success state after the redirect.
 *
 * Expected children:
 *   [data-form-state]    container with the <form>
 *   [data-success-state] hidden container shown after a successful submit
 *   [data-error]         hidden error message element
 *
 * Optional attribute:
 *   data-signup-key      stable id used to match the redirect back to the
 *                        form that was submitted (falls back to DOM index)
 *
 * Dispatches a `bh:signup:success` event when the success state is shown.
 */
class BhSignupForm extends HTMLElement {
  static PENDING_KEY = 'bh_signup_pending';

  connectedCallback() {
    this.formState = this.querySelector('[data-form-state]');
    this.successState = this.querySelector('[data-success-state]');
    this.errorEl = this.querySelector('[data-error]');
    this.form = this.formState?.querySelector('form');
    if (!this.form) return;

    this.key = this.dataset.signupKey || `signup-${BhSignupForm.indexOf(this)}`;
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.form.addEventListener('submit', () => this.onSubmit());

    if (this.wasJustPosted()) this.showSuccess();
  }

  static indexOf(el) {
    return Array.prototype.indexOf.call(document.querySelectorAll('bh-signup-form'), el);
  }

  onSubmit() {
    // Do not preventDefault: let the browser / Shopify captcha submit the
    // form. Just remember which form is in flight and show a busy state.
    this.hideError();
    try {
      sessionStorage.setItem(BhSignupForm.PENDING_KEY, this.key);
    } catch {
      /* sessionStorage unavailable */
    }

    if (this.submitButton) {
      const originalText = this.submitButton.value || this.submitButton.textContent;
      this.setButtonText('Sending...');
      // If the captcha challenge is dismissed the page never reloads;
      // give the button back after a moment so the visitor can retry.
      setTimeout(() => this.setButtonText(originalText), 8000);
    }
  }

  setButtonText(text) {
    if (!this.submitButton) return;
    if (this.submitButton.tagName === 'INPUT') {
      this.submitButton.value = text;
    } else {
      this.submitButton.textContent = text;
    }
  }

  wasJustPosted() {
    if (new URLSearchParams(window.location.search).get('customer_posted') !== 'true') return false;

    let pending = null;
    try {
      pending = sessionStorage.getItem(BhSignupForm.PENDING_KEY);
    } catch {
      /* sessionStorage unavailable */
    }

    if (pending) {
      if (pending !== this.key) return false;
      try {
        sessionStorage.removeItem(BhSignupForm.PENDING_KEY);
      } catch {
        /* ignore */
      }
      return true;
    }

    // No record of which form was submitted (storage blocked): only claim
    // the success when this is the sole signup form on the page.
    return document.querySelectorAll('bh-signup-form').length === 1;
  }

  showSuccess() {
    this.formState.hidden = true;
    if (this.successState) this.successState.hidden = false;

    try {
      localStorage.setItem('bh_email_popup', JSON.stringify({ subscribed: true }));
    } catch {
      /* localStorage unavailable */
    }

    this.dispatchEvent(new CustomEvent('bh:signup:success', { bubbles: true }));
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

customElements.define('bh-signup-form', BhSignupForm);
